import React from 'react';
import { useNavigate } from 'react-router';

import Box from '@amzn/awsui-components-console/box';
import Icon from '@amzn/awsui-components-console/icon';
import Link, { LinkProps } from '@amzn/awsui-components-console/link';
import SpaceBetween from '@amzn/awsui-components-console/space-between';

import styles from './StackedBreadcrumbGroup.module.scss';
import { SEARCH_PATH } from 'src/common/routes';
import { useTranslation } from 'src/i18n/shim/useTranslation';

export interface StackedBreadcrumbItem {
  /** Primary text displayed on the top line (optional — omit for single-line items) */
  readonly topText?: string;
  /** Secondary text displayed on the bottom line (required) */
  readonly bottomText: string;
  /** URL for navigation; if omitted, the item renders as an inert '#' link */
  readonly href?: string;
  /** Click handler for the item (receives the Cloudscape Link follow event) */
  readonly onClick?: (event: CustomEvent<LinkProps.FollowDetail>) => void;
}

export interface StackedBreadcrumbGroupProps {
  /** Breadcrumb items. The last item is treated as the current page (non-clickable). */
  readonly items: readonly StackedBreadcrumbItem[];
  /**
   * Called when a breadcrumb link is followed.
   *
   * Navigation is already client-side by default (react-router `navigate`), so
   * this is only needed for side effects such as metrics or unsaved-changes
   * guards. Call `event.preventDefault()` to take over navigation entirely —
   * the component then skips its own `navigate()` call.
   *
   * `event.detail.href` contains the target href.
   */
  readonly onFollow?: (event: CustomEvent<LinkProps.FollowDetail>) => void;
}

/**
 * Root breadcrumb, prepended to every trail. Points at the search page rather
 * than ROOT_PATH: ROOT_PATH is a probe route that, for a user with any
 * namespace access, only redirects here anyway after a listNamespaces round
 * trip, and HOME_PATH renders the marketing landing shell outside the app
 * chrome, so neither is a useful destination from inside the app.
 */
const HOME_HREF = SEARCH_PATH;

/**
 * A breadcrumb group where each item displays two stacked lines of text.
 * Composes Cloudscape Box, Link, Icon, and SpaceBetween for minimal custom CSS.
 */
const StackedBreadcrumbGroup: React.FC<StackedBreadcrumbGroupProps> = ({
  items,
  onFollow,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Built per render rather than at module scope: its label is localized, so it
  // cannot be resolved before a component is mounted under the bundle provider.
  const homeItem: StackedBreadcrumbItem & { readonly href: string } = {
    bottomText: t('breadcrumb-home'),
    href: HOME_HREF,
  };
  const allItems = [homeItem, ...items];

  /**
   * Builds the `onFollow` handler for a single breadcrumb link.
   *
   * Cloudscape only fires `onFollow` for an unmodified left-click — Cmd/Ctrl/
   * Shift/middle clicks fall through to the native anchor — so calling
   * `preventDefault()` here suppresses the full page reload while still
   * letting "open in new tab" work off the real `href`.
   */
  const buildFollowHandler =
    (item: StackedBreadcrumbItem) =>
    (event: CustomEvent<LinkProps.FollowDetail>) => {
      item.onClick?.(event);
      onFollow?.(event);

      // A consumer that called preventDefault() owns navigation; don't double-navigate.
      if (event.defaultPrevented) {
        return;
      }

      // Never let the native anchor follow through — that would be a full reload.
      event.preventDefault();

      const href = event.detail.href ?? item.href;
      // '#' / '' are placeholder hrefs for non-navigable items.
      if (!href || href === '#') {
        return;
      }

      navigate(href);
    };

  return (
    <nav aria-label={t('breadcrumbs-nav-aria')} className={styles.nav}>
      <div className={styles.row}>
        {allItems.map((item, index) => {
          const isLast = index === allItems.length - 1;
          const isHome = index === 0;
          const key = `${item.href ?? ''}-${item.bottomText}`;
          const isSingleLine = !item.topText;

          return (
            <React.Fragment key={key}>
              {index > 0 && (
                <div className={styles.separator}>
                  <Box color="text-status-inactive">
                    <Icon name="angle-right" size="x-small" />
                  </Box>
                </div>
              )}
              {isHome ? (
                /* Home item: single centered line, no placeholder */
                <span
                  className={styles.singleLineItem}
                  data-testid="breadcrumb-home"
                >
                  <Link
                    href={homeItem.href}
                    onFollow={buildFollowHandler(item)}
                    fontSize="body-s"
                  >
                    {item.bottomText}
                  </Link>
                </span>
              ) : (
                <span
                  aria-current={isLast ? 'page' : undefined}
                  className={isSingleLine ? styles.singleLineItem : undefined}
                  data-testid="breadcrumb-item"
                >
                  <SpaceBetween direction="vertical" size="xxxs">
                    {item.topText ? (
                      <Box
                        variant="span"
                        color="text-body-secondary"
                        fontSize="body-s"
                      >
                        {item.topText}
                      </Box>
                    ) : (
                      <span aria-hidden="true">
                        {/* Empty top line to maintain alignment — hidden from assistive tech */}
                        &nbsp;
                      </span>
                    )}
                    {isLast ? (
                      <Box variant="span" fontWeight="bold" fontSize="body-s">
                        {item.bottomText}
                      </Box>
                    ) : (
                      <Link
                        href={item.href ?? '#'}
                        onFollow={buildFollowHandler(item)}
                        fontSize="body-s"
                      >
                        {item.bottomText}
                      </Link>
                    )}
                  </SpaceBetween>
                </span>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </nav>
  );
};

export default StackedBreadcrumbGroup;
